import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { scheduleStore, type Schedule } from '@/pinia/stores/schedule/scheduleStore';
import EventBus from '@/components/mixins/event-bus-mixin';
import type ApiError from '@/globals/api-error';
import type { AxiosError } from 'axios';

/**
 * Composable for fetching and providing the list of user schedules on the media page.
 *
 * Wraps the shared scheduleStore so callers do not need to import the store directly.
 */
export function useUserSchedules () {
  const $apiError = inject('$apiError') as typeof ApiError;
  const { t } = useI18n();
  const store = scheduleStore();

  /** Reactive list of all fetched schedules. */
  const allSchedules = computed(() => store.schedules);

  /** True while schedules are being fetched from the API. */
  const isFetchingSchedules = computed(() => store.isFetching);

  /**
   * Surfaces a fetch error to the user, known HTTP errors go through $apiError,
   * everything else through the global EventBus notification.
   * @param {*} error
   */
  function handleErrorNotification (error: AxiosError) {
    if (!error) return;

    if (error?.response?.status) {
      $apiError.handleAPIRequestError(error, {
        showDialogNotification: true,
      });
    } else {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('playerSetup.UnknownError'),
        },
        callbacks: {
          eventOnClose: () => {},
        },
      });
    }
  }

  /**
   * Fetches all schedules via paginated API calls and populates the store.
   * @param {boolean} isAlert - Pass true to fetch alert-type schedules instead.
   * @returns {Promise<{success: boolean, data?: Array, error?: *}>}
   */
  async function fetchSchedules (isAlert: boolean = false): Promise<{
    success: boolean;
    data?: Schedule[];
    error?: AxiosError
  }> {
    const result = await store.fetchAllSchedules(isAlert);

    if (!result.success && result.error) {
      handleErrorNotification(result.error);
    }

    return result;
  }

  return {
    allSchedules,
    isFetchingSchedules,

    fetchSchedules,
  };
}
